import React from 'react';
import { Box, Link, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../../utils/responsiveMedia';
import SalonTemplateAbout from '../blocks/SalonTemplateAbout';
import SalonTemplateContactLocation from '../blocks/SalonTemplateContactLocation';
import SalonTemplateGallery from '../blocks/SalonTemplateGallery';
import SalonTemplateIdentity from '../blocks/SalonTemplateIdentity';
import SalonTemplateOpeningHours from '../blocks/SalonTemplateOpeningHours';
import SalonTemplateReviews from '../blocks/SalonTemplateReviews';
import SalonTemplateServices from '../blocks/SalonTemplateServices';
import SalonTemplateTeam from '../blocks/SalonTemplateTeam';


const MaisonEclatTemplate = ({
  viewModel,
  actions,
  mode = 'public',
  viewport = 'desktop',
  direction = 'ltr',
}) => {
  const { t } = useTranslation();
  const { layout, tokens: theme } = useSalonPublicTheme();
  const identity = viewModel?.identity || {};
  const photos = Array.isArray(viewModel?.photos) ? viewModel.photos : [];
  const reviews = Array.isArray(viewModel?.reviews) ? viewModel.reviews : [];
  const categories = Array.isArray(viewModel?.serviceCategories)
    ? viewModel.serviceCategories.filter((category) => (
      Array.isArray(category?.services) && category.services.length > 0
    ))
    : [];
  const navigationCategories = categories.filter((category) => (
    typeof category?.name === 'string' && category.name.trim()
  ));
  const publicState = viewModel?.publicState || {};
  const visibility = layout.tokens.visibility;
  const mobile = viewport === 'mobile';

  return (
    <Box
      data-testid="salon-template-renderer"
      data-salon-template="maison_eclat"
      data-mode={mode}
      data-viewport={viewport}
      dir={direction}
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        backgroundColor: theme.salon.background,
      }}
    >
      <SalonTemplateIdentity
        data={{
          ...identity,
          heroPhoto: photos[0]?.src,
          averageRating: viewModel?.averageRating,
          reviewCount: reviews.length,
          isFavorite: viewModel?.capabilities?.isFavorite,
        }}
        variant="split"
        mode={mode}
        onBack={actions?.onBack}
        onToggleFavorite={actions?.onToggleFavorite}
        onOpenMobileInfo={actions?.onOpenMobileInfo}
        onOpenHeaderInfo={actions?.onOpenHeaderInfo}
        onBook={actions?.onOpenBooking}
        onContact={actions?.onContact}
        clientChatEnabled={identity.raw?.client_chat_enabled}
      />

      <Box
        data-testid="maison-eclat-scroll-region"
        sx={{
          flex: 1,
          minHeight: mobile ? 'auto' : 0,
          width: '100%',
          overflowY: mobile ? 'visible' : 'auto',
          overflowX: 'hidden',
          [HIGH_DENSITY_TABLET_MEDIA]: { overflowY: 'visible' },
        }}
      >
        <Box
          component="main"
          data-testid="maison-eclat-content"
          data-layout={mobile ? 'single-column' : 'photo-content-split'}
          sx={{
            display: 'grid',
            gridTemplateColumns: mobile
              ? 'minmax(0, 1fr)'
              : 'minmax(0, 7fr) minmax(300px, 5fr)',
            columnGap: { xs: 0, md: 6 },
            width: '100%',
            maxWidth: 1440,
            mx: 'auto',
            px: { xs: 2, md: 5 },
            py: { xs: 3, md: 7 },
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <SalonTemplateAbout data={identity} variant="panel" />
            {navigationCategories.length > 0 ? (
              <Box
                component="nav"
                aria-label={t('landing_salon_details.our_services')}
                data-salon-template-block="categories"
                data-sticky={String(!mobile)}
                sx={{
                  position: mobile ? 'static' : 'sticky',
                  top: mobile ? 'auto' : 24,
                  alignSelf: 'start',
                  zIndex: 2,
                  py: 2,
                }}
              >
                <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                  {navigationCategories.map((category) => (
                    <Link
                      key={category.id}
                      href={`#maison-eclat-category-${category.id}`}
                      underline="hover"
                      sx={{ color: theme.salon.primary, fontWeight: 700 }}
                    >
                      {category.name}
                    </Link>
                  ))}
                </Stack>
              </Box>
            ) : null}
            <SalonTemplateServices
              data={categories}
              variant="cards"
              mode={mode}
              loading={Boolean(publicState.servicesLoading)}
              onBookService={actions?.onBookService}
              categoryAnchorPrefix="maison-eclat-category"
            />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <SalonTemplateGallery
              data={photos}
              salonId={identity.id}
              variant="secondary"
              visible={visibility.gallery}
              mode={mode}
              onSelectPhoto={actions?.onSelectPhoto}
              onNavigatePhoto={actions?.onNavigatePhoto}
              selectedIndex={publicState.photoIndex}
            />
            <SalonTemplateReviews
              data={{
                items: reviews,
                averageRating: viewModel?.averageRating,
                isAuthenticated: Boolean(publicState.isAuthenticated),
                isLoading: Boolean(publicState.reviewsLoading),
                error: publicState.reviewsError || null,
              }}
              variant="quotes"
              visible={visibility.reviews}
              mode={mode}
              onOpenReviewComposer={actions?.onOpenReviewComposer}
              onOpenAllReviews={actions?.onOpenAllReviews}
            />
            <SalonTemplateTeam
              data={viewModel?.team}
              variant="cards"
              visible={visibility.team}
              mode={mode}
              onSelectMember={actions?.onSelectTeamMember}
            />
            <SalonTemplateOpeningHours
              data={{ hours: viewModel?.openingHours, closures: viewModel?.closures }}
              variant="card"
              visible={visibility.opening_hours}
              markClosuresAsBlock={false}
            />
          </Box>

          <Box sx={{ minWidth: 0, gridColumn: mobile ? '1' : '1 / -1' }}>
            <SalonTemplateContactLocation
              data={identity}
              variant="split"
              visible={visibility.location}
              mode={mode}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MaisonEclatTemplate;
